/**
 * Shared constants ported from legacy `environment.commons.ts` (only what picker, customer,
 * auth and monitoring use).
 */

export enum Status {
  PENDING = 1,
  PROGRESS = 2,
  FINISHED = 3,
  DELIVERED = 4,
  CANCELLED = 5,
}

export enum StatusPicking {
  PENDING = 1,
  PROGRESS = 2,
  FINISHED = 3,
  DELIVERED = 4,
  CANCELLED = 5,
}

/** Cancellation reasons (rt_platform). */
export enum CancellationReason {
  CUSTOMER_WILL_RETURN = 1,
  ORDER_WITHOUT_QR = 4,
}

/** ServiceEnum ids used when creating attentions (withdrawalModality route param). */
export enum ServiceId {
  WITHDRAW = 4,
  WITHDRAW_CAR = 8,
}

/** Values written to Firebase `statusPickingBoy` / `statusPostSale`. */
export type QueueStatus = 'pending' | 'progress' | 'cancelled' | 'finished' | 'delivered';

export const productCode = 'RT';

export enum Roles {
  ADMIN = 'ADMIN',
  CORPORATE_ADMIN = 'CORPORATE_ADMIN',
  CORPORATE_SUPERVISOR = 'CORPORATE_SUPERVISOR',
  CORPORATE_AGENT = 'CORPORATE_AGENT',
  STORE_SECURITY = 'STORE_SECURITY',
}

export enum Modules {
  ORDER_ATTENTION = 'order_attention',
  QR_VALIDATION_SECURITY = 'qr_validation_security',
  PICKUP_CHECKIN = 'pickup_checkin',
  OPERATIONS_MONITORING = 'operations_monitoring',
  QUEUE_MANAGEMENT = 'queue_management',
}

/** Module selection options. Labels and order are the legacy ones. */
export const ModuleLabels: Array<{ value: Modules; label: string }> = [
  { value: Modules.ORDER_ATTENTION, label: 'Atención de pedidos' },
  { value: Modules.QR_VALIDATION_SECURITY, label: 'Validación de QR - Seguridad' },
  { value: Modules.PICKUP_CHECKIN, label: 'Retiro en tienda - Cliente' },
  { value: Modules.OPERATIONS_MONITORING, label: 'Monitoreo y gestión' },
  { value: Modules.QUEUE_MANAGEMENT, label: 'Gestor de colas' },
];

export const accessRoutes: Record<Modules, Partial<Record<Roles, string>>> = {
  [Modules.OPERATIONS_MONITORING]: {
    [Roles.ADMIN]: '/monitoring',
    [Roles.CORPORATE_ADMIN]: '/monitoring',
    [Roles.CORPORATE_SUPERVISOR]: '/monitoring',
  },
  [Modules.QR_VALIDATION_SECURITY]: {
    [Roles.STORE_SECURITY]: '/security',
  },
  [Modules.ORDER_ATTENTION]: {
    [Roles.CORPORATE_AGENT]: '/picking',
  },
  [Modules.PICKUP_CHECKIN]: {},
  [Modules.QUEUE_MANAGEMENT]: {},
};

export const imagesWithdrawalDetail: Array<{ id: number; name: string; description: string }> = [
  { id: 0, name: 'imagensustento1', description: '' },
  { id: 1, name: 'imagensustento2', description: '' },
  { id: 2, name: 'imagensustento3', description: '' },
  { id: 3, name: 'imagensustento4', description: '' },
  { id: 4, name: 'imagensustento5', description: '' },
  { id: 5, name: 'imagensustento6', description: '' },
];

export enum CustomerLoginInputType {
  DOCUMENT = 'DOCUMENT',
  ORDER_ID = 'ORDER_ID',
}

/**
 * Single-field detection for the public customer login. Mirrors what cc-api-orders accepts.
 */
export const customerLoginRegex = {
  document: /^\w+$/,
  /** VTEX orderId: 13dig-2dig | v<n>(plzv|viv|pvqa|vivqa)-2dig */
  orderId: /^(\d{13,}-\d{2}|v\d+(plzv|viv|pvqa|vivqa)-\d{2})$/i,
};

/** Backend session cookie TTL (12 hours). */
export const SESSION_COOKIE_TTL_MS = 12 * 60 * 60 * 1000;
/** Trigger session refresh this many ms before expiry (2 hours). */
export const SESSION_REFRESH_OFFSET_MS = 2 * 60 * 60 * 1000;

/** localStorage keys (identical to legacy). */
export const StorageKeys = {
  AUTH_USER: 'auth_user',
  AUTH_MODULE: 'auth_module',
  AUTH_SESSION_ISSUED: 'auth_session_issued_at',
  MODULE_SELECTED: 'module-selected',
  WITHDRAW_STORE: 'withdraw_store',
  PICKING_REMEMBER_NAME: 'picking_remember_name',
  /** Legacy MonitoringTenantService.STORAGE_KEY, cleared on logout. */
  MONITORING_TENANT: 'monitoring_tenant_selected',
  opsKey: (documentNumber: string, groupId: string) => `ops_${documentNumber}_${groupId}`,
  photosKey: (documentNumber: string, groupId: string) => `photos_${documentNumber}_${groupId}`,
} as const;

/** Email domains allowed to recover password (legacy domainsAllowedInRegister). */
export const domainsAllowedInRegister: string[] = [
  '@promart.pe',
  '@promart.ec',
  '@spsa.pe',
  '@oechsle.pe',
  '@realplaza.com.pe',
  '@irdigital.pe',
  '@indigitalxp.pe',
  '@plazavea.pe',
  '@oslo.pe',
  '@tiendasmass.pe',
  '@exacta.pe',
  '@transportesfart.com',
  '@anclogistica.com.pe',
  '@bastis.pe',
  '@parauco.com',
  '@mallplaza.com',
];

// ---- monitoring (legacy environment.commons) ----

/** Page size of the monitoring lists (legacy `limitPerPage`). */
export const limitPerPage = 10;

/** Sales channels of the monitoring filters (legacy `saleMonitoring`). */
export const saleMonitoring: Array<{ value: string; viewValue: string }> = [
  { value: 'Venta Empresa', viewValue: 'Venta Empresa' },
  { value: 'Ecommerce', viewValue: 'Ecommerce' },
  { value: 'Retail', viewValue: 'Retail' },
  { value: 'Seller', viewValue: 'Seller' },
];

/** Monitoring filter screens that show the "Tiempo" field (legacy `typeSearchWithTimeField`). */
export const typeSearchWithTimeField: string[] = ['timeAttention', 'usageRt'];

/** Monitoring filter screens that show the "Uso de RT" field (legacy `typeSearchUsedRTField`). */
export const typeSearchUsedRTField: string[] = ['searchSalesNote'];

/** Pickup point withdrawal types (legacy `withdrawalTypes` enum). */
export enum WithdrawalTypes {
  PICK_UP_IN_STORE = 'Retiro en tienda',
  CAR_PICK_UP = 'Retiro en auto',
}

export enum CustomerSearchType {
  IDENTITY_DOCUMENT = 'IDENTITY_DOCUMENT',
  EMAIL = 'EMAIL',
  ORDER_ID = 'ORDER_ID',
  SALE_NOTE_NUMBER = 'SALE_NOTE_NUMBER',
  REQUEST_NUMBER = 'REQUEST_NUMBER',
}

/** Generic search patterns of the monitoring topbar (legacy `regexTypeData`, same order). */
export const regexTypeData: Map<string, RegExp> = new Map([
  ['email', /^[a-zA-Z0-9][\w\.-]*[a-zA-Z0-9]@[a-zA-Z0-9][\w\.-]*[a-zA-Z0-9]\.[a-zA-Z]{2,3}?$/],
  ['id', /^[A-Za-z0-9]{8,12}$/], // dni | pasaporte | ruc(11) - carnet de extranjeria(12)
  ['request', /^(PVEA|VIV|RPLAZA|HPSA|TPSA|AGORA|MKR|AGORA_SHOP|JOKR|SHPSTAR)-(CD|ST)-[A-Za-z0-9]{8,12}-[0-9]+$/],
  ['order', /^((\d{13}-\d{2})|(\d{15})|(v\d+viv-\d{2})|(v\d+plzv-\d{2}|(v\d+pvqa-\d{2})|(v\d+vivqa-\d{2})))?$/],
  ['sale-note', /^\d{13}?$/],
]);

export const searchTypeMap: Map<string, CustomerSearchType> = new Map([
  ['email', CustomerSearchType.EMAIL],
  ['id', CustomerSearchType.IDENTITY_DOCUMENT],
  ['request', CustomerSearchType.REQUEST_NUMBER],
  ['order', CustomerSearchType.ORDER_ID],
  ['sale-note', CustomerSearchType.SALE_NOTE_NUMBER],
]);
